import {useCallback} from 'react';

import {ProcedureFlowchart} from 'procedures/ProcedureDomainTypes';
import {ModifyFlowchartCallback} from 'procedures/ProcedurePage';
import {
  ProcedurePageContext,
  BlockingSaveObjectSource,
  BlockingSaveOperation,
} from 'procedures/ProcedurePageContext';
import getProcedureUIString, {ProceduresStringKeys} from 'procedures/ProceduresStringKeys';

import {
  deleteMethodFromLinearFlowchart,
  duplicateMethodFromLinearFlowchart,
  changeMethodName,
  addMethodToLinearFlowchart,
  deleteMaterialEdge,
  AppendDirection,
  generateNewMethodCid,
} from './flowchartManagementUtils';
import {
  MethodNodeCallbacks,
  MethodEdgeCallbacks,
  MaterialEdgeCallbacks,
} from './ProcedureReactFlowCanvasTypes';

export interface ProcedureFlowchartEditorCustomCallbacksParams {
  methodNodeCallbacks: MethodNodeCallbacks;
  methodEdgeCallbacks: MethodEdgeCallbacks;
  materialEdgeCallbacks: MaterialEdgeCallbacks;
}

export type UseProcedureFlowchartEditorCustomCallbacksOutput = ProcedureFlowchartEditorCustomCallbacksParams;

/**
 * Encapsulates all callbacks needed for our custom Nodes and Edges within a ProcedureEditor.
 * Does not currently include base callbacks that are passed as props directly to ReactFlow
 * (ex: onConnect, onNodesChange, onDrag)
 *
 * Note that the two prop callbacks (setFlowchart, startEditMethod) should be cached outside this hook, to
 * ensure the hook doesn't fire unnecessarily.
 */
export function useProcedureFlowchartEditorCustomCallbacks(props: {
  modifyFlowchart: (cb: ModifyFlowchartCallback) => void;
  startEditMethod: (nodeId: string) => void;
  autofocusEditNameOnNewMethod: (newMethodCid: string) => void;
}): UseProcedureFlowchartEditorCustomCallbacksOutput {
  const {modifyFlowchart, startEditMethod, autofocusEditNameOnNewMethod} = props;
  const {saveImmediately, procedureType} = ProcedurePageContext.useContext();

  const handleDuplicateNode = useCallback(
    async (nodeId: string): Promise<void> => {
      const methodUIString = getProcedureUIString(ProceduresStringKeys.METHOD, procedureType);
      await saveImmediately((latestFlowchart: ProcedureFlowchart) => {
        const newMethodCid = generateNewMethodCid();
        const {newFlowchart, cloneStepCidToDocumentVersionIdMap} = duplicateMethodFromLinearFlowchart(
          latestFlowchart,
          nodeId,
          newMethodCid,
          procedureType
        );
        modifyFlowchart(() => newFlowchart);
        autofocusEditNameOnNewMethod(newMethodCid);
        return {
          cloneStepCidToDocumentVersionIdMap,
          blockingSaveObjectSource: BlockingSaveObjectSource.Method,
          blockingSaveOperation: BlockingSaveOperation.Duplicate,
        };
      }, `Duplicating ${methodUIString}...`);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [modifyFlowchart, autofocusEditNameOnNewMethod]
  );

  const handleChangeMethodName = useCallback(
    (nodeId: string, newMethodName: string) => {
      modifyFlowchart((fc) => changeMethodName(fc, nodeId, newMethodName, procedureType));
    },
    [modifyFlowchart, procedureType]
  );

  const handleAddMethod = useCallback(
    (sourceMethodNodeCid: string, appendDirection: AppendDirection) => {
      const newMethodCid = generateNewMethodCid();
      modifyFlowchart((fc) => {
        const {updatedFlowchart} = addMethodToLinearFlowchart(procedureType, fc, sourceMethodNodeCid, {
          appendDirection,
          newMethodCid,
        });
        autofocusEditNameOnNewMethod(newMethodCid);
        return updatedFlowchart;
      });
    },
    [procedureType, modifyFlowchart, autofocusEditNameOnNewMethod]
  );

  const handleDeleteMethod = useCallback(
    (methodCid: string) => {
      modifyFlowchart((fc) => deleteMethodFromLinearFlowchart(fc, methodCid));
    },
    [modifyFlowchart]
  );

  const handleDeleteMaterialEdge = useCallback(
    (edgeId: string) => {
      modifyFlowchart((fc) => deleteMaterialEdge(fc, edgeId));
    },
    [modifyFlowchart]
  );

  return {
    methodNodeCallbacks: {
      onDuplicateClick: handleDuplicateNode,
      onChangeMethodName: handleChangeMethodName,
      onAddMethod: handleAddMethod,
      onEditMethod: startEditMethod,
      onDeleteMethod: handleDeleteMethod,
    },
    methodEdgeCallbacks: {
      onAddMethodClick: handleAddMethod,
    },
    materialEdgeCallbacks: {
      onDeleteMaterialEdge: handleDeleteMaterialEdge,
    },
  };
}
